import { Eye, EyeOff, LockKeyhole, ShieldCheck, UserRound } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { AuthStep } from '../api/auth';
import { BrandMark } from '../components/BrandMark';
import { LoadingState } from '../components/StatePanel';
import { TotpEnrollment } from '../components/TotpEnrollment';
import { useAuth } from '../context/AuthContext';

export function LoginPage() {
  const { isAuthenticated, isLoading, isInitializing, login, verifyLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState<AuthStep | null>(null);
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState('');
  const redirectTo = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/cameras';
  if (isInitializing) return <LoadingState />;
  if (isAuthenticated) return <Navigate to={redirectTo} replace />;

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault(); setError('');
    try {
      const next = await login(username, password);
      if (next) { setStep(next); setCode(''); setTrustDevice(false); setRememberDevice(false); }
      else navigate(redirectTo, { replace: true });
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Не удалось войти.'); }
  };

  const submitCode = async (event: FormEvent) => {
    event.preventDefault(); setError('');
    if (!step) return;
    try { await verifyLogin(step, code.trim(), trustDevice, rememberDevice); navigate(redirectTo, { replace: true }); }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'Неверный или просроченный код.'); }
  };

  return <main className="login-page"><div className="login-page__backdrop" aria-hidden="true" />
    <section className="login-panel"><div className="login-panel__inner"><BrandMark />
      {!step && <><div className="login-heading"><h1>Вход в систему</h1><p>Введите логин и пароль</p></div>
        <form className="auth-form" onSubmit={(event) => void submitPassword(event)}>
          <div className="field-group"><label htmlFor="login">Логин</label><div className="input-shell"><UserRound size={18} aria-hidden="true" />
            <input id="login" autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} required /></div></div>
          <div className="field-group"><label htmlFor="password">Пароль</label><div className="input-shell"><LockKeyhole size={18} aria-hidden="true" />
            <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password}
              onChange={(event) => setPassword(event.target.value)} required />
            <button className="input-action" type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}>
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></div></div>
          <button className="button button--primary button--large button--full" disabled={isLoading}>Войти</button>
        </form><div className="login-links"><Link className="field-link" to="/activate">Активировать учётную запись</Link></div></>}
      {step?.kind === 'twoFactor' && <><div className="login-heading"><h1>Подтверждение входа</h1><p>{rememberDevice
        ? 'Для запоминания устройства введите свежий код приложения-аутентификатора. Для входа с кодом восстановления отключите запоминание устройства.'
        : 'Введите код из приложения-аутентификатора или одноразовый код восстановления.'}</p></div>
        <form className="auth-form" onSubmit={(event) => void submitCode(event)}>
          <div className="field-group"><label htmlFor="second-factor">Код</label><div className="input-shell"><ShieldCheck size={18} aria-hidden="true" />
            <input id="second-factor" autoComplete="one-time-code" value={code} onChange={(event) => setCode(event.target.value)} required autoFocus /></div></div>
          <label className="login-remember"><input type="checkbox" checked={trustDevice} onChange={(event) => setTrustDevice(event.target.checked)} /> Доверять этому устройству 30 дней</label>
          <label className="login-remember"><input type="checkbox" checked={rememberDevice} onChange={(event) => setRememberDevice(event.target.checked)} /> Запомнить это устройство</label>
          <button className="button button--primary button--large button--full" disabled={isLoading}>Подтвердить</button>
        </form></>}
      {step?.kind === 'enroll' && <TotpEnrollment step={step} onDone={() => { setStep(null); setPassword(''); }} />}
      {step && <button className="back-button" type="button" onClick={() => { setStep(null); setError(''); }}>Назад ко входу</button>}
      {error && <div className="form-message form-message--error" role="alert">{error}</div>}
    </div></section>
    <footer className="login-status-bar"><span><i aria-hidden="true" /> API подключен</span><span><ShieldCheck size={17} aria-hidden="true" /> Защищённое соединение</span></footer>
  </main>;
}
